import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonService } from '@services/common.service';
import { forkJoin, Subscription } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SearchProfileService } from '@services/search-profile.service';
import { GoldenProfile } from '@models/GoldenProfile';
import { SourceCrossReference } from '@models/CrossReference';
import { Source, SourceApiResponse } from '@models/Source';
import {
  ColumnPreference,
  ColumnsModelApiResponse,
  SearchColumnResults,
} from '@models/ColumnsModel';
import { AppConstants } from '@constants/app.constant';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ResultProfileComponent } from '../result-profile/result-profile.component';
import { LoaderComponent } from '@components/shared/loader/loader.component';
import { SearchHistoryComponent } from '../search-history/search-history.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BulkSearchModalComponent } from '../bulk-search-modal/bulk-search-modal.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ViewChild } from '@angular/core';
export function alphanumericValidator(): ValidatorFn {
  const regex = /^[a-zA-Z0-9\s]*$/;
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    if (!regex.test(control.value)) {
      return { alphanumeric: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-search-profile',
  templateUrl: './search-profile.component.html',
  styleUrl: './search-profile.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    ResultProfileComponent,
    LoaderComponent,
    SearchHistoryComponent,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class SearchProfileComponent implements OnInit, OnDestroy {
  modalService = inject(NgbModal);
  cdr = inject(ChangeDetectorRef);
  viewScreen = '';
  commonService = inject(CommonService);
  fb = inject(FormBuilder);
  searchProfileService = inject(SearchProfileService);
  constants = AppConstants;
  displaySourceError = true;
  columnModal: {
    type: string;
    db_column_name: string;
    ui_column_name: string;
  }[] = [];
  sourceList: Source[] = [];
  allSources: { source: string; type: string }[] = [];
  isSearchFormValid = false;
  goldenProfilesMp!: MatTableDataSource<GoldenProfile>;
  sourceCrossReferencesMp!: MatTableDataSource<SourceCrossReference>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('rPaginator') rPaginator!: MatPaginator;
  readonly hiddenSources = ['Shift', 'RMUS', 'Legacy SAP CRM'];
  totalItems = 0;
  rTotalItems = 0;
  spHistorySubscription!: Subscription;
  searchFormSubscription!: Subscription;
  bsBackSubscription!: Subscription;
  goldenProfiles: GoldenProfile[] = [];
  sourceCrossReferences: SourceCrossReference[] = [];
  searchForm: FormGroup = this.fb.group({
    rcm_id: new FormControl('', [
      Validators.pattern('^[0-9]*$'),
      Validators.maxLength(10),
      Validators.minLength(10),
    ]),
    ishift: new FormControl('', [Validators.pattern('^[a-zA-Z0-9-]*$')]),

    rmus: new FormControl('', [Validators.pattern('^[a-zA-Z0-9-]*$')]),

    crm: new FormControl('', [Validators.pattern('^[a-zA-Z0-9-]*$')]),
    name: new FormControl('', [
      Validators.pattern("^[a-zA-Z0-9-,.()&' ]*$"),
      Validators.minLength(2),
    ]),
    address: new FormControl('', [
      Validators.pattern('^[a-zA-Z0-9-,.& ]*$'),
      Validators.minLength(2),
    ]),
    dea: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9,.\-\s]+$/),
      Validators.maxLength(9),
      Validators.minLength(7),
    ]),
    hin_no: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9,.\-\s]+$/),
      Validators.maxLength(9),
      Validators.minLength(2),
    ]),
    id_340b: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9,.\-\s]+$/),
      Validators.maxLength(11),
      Validators.minLength(2),
    ]),
    npi: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9,.\-\s]+$/),
      Validators.maxLength(10),
      Validators.minLength(10),
    ]),
    sln: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9,.\-\s]+$/),
      Validators.minLength(2),
    ]),
    city: new FormControl('', [
      Validators.pattern('^[a-zA-Z0-9-,. ]*$'),
      Validators.minLength(2),
    ]),
    state: new FormControl('', [
      Validators.pattern('^[a-zA-Z0-9-,. ]*$'),
      Validators.minLength(2),
    ]),
    zip: new FormControl('', [
      Validators.pattern('^[0-9]{5}(?:-[0-9]{4})?$'),
      Validators.minLength(5),
    ]),
    type: new FormControl('Both'),
    source: new FormControl(''),
    source_id: new FormControl('', [Validators.pattern('^[a-zA-Z0-9-]*$')]),
    tddd: new FormControl('', [
      Validators.pattern('^[a-zA-Z0-9-,.]*$'),
      Validators.minLength(2),
    ]),
    hcce: new FormControl('', [
      Validators.pattern('^[a-zA-Z0-9-,.]*$'),
      Validators.minLength(2),
    ]),
  });
  showLoader = this.commonService.showLoader;
  colPref = { cross_reference_columns: '', golden_profile_columns: '' };

  searchParams: { display: string; value: string }[] = [];

  types = [{ type: 'Both' }, { type: 'HCP' }, { type: 'HCA' }];

  ngOnDestroy(): void {
    if (this.bsBackSubscription) {
      this.bsBackSubscription.unsubscribe();
    }
    if (this.searchFormSubscription) {
      this.searchFormSubscription.unsubscribe();
    }
    if (this.spHistorySubscription) {
      this.spHistorySubscription.unsubscribe();
    }
  }
  ngOnInit(): void {
    const navState = history.state;
    if (navState?.fromAffiliationBack) {
      this.goldenProfiles = [...this.commonService.savedGoldenProfiles];

      this.sourceCrossReferences = [
        ...this.commonService.savedSourceCrossReferences,
      ];

      this.searchParams = [...this.commonService.savedSearchParams];

      this.viewScreen = 'Result';

      setTimeout(() => {
        this.updateTableData();
        this.cdr.detectChanges();
      });

      return;
    }

    if (navState?.searchPayload) {
      this.searchForm.patchValue(navState.searchPayload);

      setTimeout(() => {
        this.searchResult();
        this.viewScreen = 'Result';
      });
    }
    this.commonService.setShowLoader(true);
    const r1 = this.searchProfileService.getSources();
    const r2 = this.searchProfileService.getColumns();
    const r3 = this.searchProfileService.getUserColumnPreference();
    forkJoin([r1, r2, r3]).subscribe(
      (
        result: [SourceApiResponse, ColumnsModelApiResponse, ColumnPreference],
      ) => {
        this.allSources = result[0].data;

        this.initSubscription();
        this.columnModal = result[1].data;
        this.colPref = result[2];
        this.saveToLocal();
        this.onTypeChange();
        this.commonService.setShowLoader(false);
        this.initSearchSubject();
      },
    );
  }
  public checkError = (controlName: string, errorName: string) => {
    return this.searchForm.controls[controlName].hasError(errorName);
  };
  saveToLocal() {
    this.searchProfileService.gpColPref.set(
      this.colPref.golden_profile_columns,
    );
    this.searchProfileService.crColPref.set(
      this.colPref.cross_reference_columns,
    );
    localStorage.setItem('display_cols', JSON.stringify(this.columnModal));
  }
  initSubscription() {
    this.bsBackSubscription = this.commonService.bsBackButton.subscribe(
      (res) => {
        this.viewScreen = res;
        this.commonService.navFrom = '';
      },
    );
    this.searchFormSubscription = this.searchForm.valueChanges.subscribe(() => {
      this.checkSearchValid();
    });
  }

  onTypeChange() {
    if (this.searchForm.value.type == 'Both') {
      this.sourceList = this.allSources.filter(
        (s) =>
          !(s.type === 'HCP' && s.source === 'OneKey') &&
          !(s.type === 'HCP' && s.source === 'DEA Portal') &&
          !this.hiddenSources.includes(s.source), // ✅ ADD ONLY THIS
      );
    } else {
      this.sourceList = this.allSources.filter((s: Source) => {
        return (
          s.type == this.searchForm.value.type &&
          !this.hiddenSources.includes(s.source) // ✅ ADD ONLY THIS
        );
      });
    }

    if (this.searchForm.value.type == '') {
      this.searchForm.get('source')?.setValue('');
    }
  }
  onChangeSource() {
    if (this.searchForm.get('source')!.value == '') {
      this.displaySourceError = false;
      this.searchForm.get('source_id')?.setValue('');
    }
  }
  search() {
    this.commonService.searchFlag.set(true);
    this.searchResult();
  }
  searchResult() {
    //temp code
    // if(localStorage.getItem('gp')!=undefined && localStorage.getItem('gp')!=null){
    //   this.checkAndShowResult();
    //   return;
    // }
    //temp code
    this.setSearchParams();
    if (this.searchForm.valid) {
      //this.patchColumnParams();
      this.commonService.setShowLoader(true);
      const payload = {
        ...this.searchForm.value,
        flag: this.commonService.searchFlag(),
      };

      // Map new fields to old Shift/RMUS/CRM search format
      if (payload.ishift) {
        payload.source = 'Shift';
        payload.source_id = payload.ishift;
      }

      if (payload.rmus) {
        payload.source = 'RMUS';
        payload.source_id = payload.rmus;
      }

      if (payload.crm) {
        payload.source = 'Legacy SAP CRM';
        payload.source_id = payload.crm;
      }

      this.searchProfileService.getSearchProfile(payload).subscribe({
        next: (res: SearchColumnResults) => {
          this.commonService.setShowLoader(false);

          this.goldenProfiles = [];
          this.sourceCrossReferences = [];

          setTimeout(() => {
            this.goldenProfiles = res.golden_profile_results || [];
            this.sourceCrossReferences = res.cross_reference_results || [];

            this.commonService.savedGoldenProfiles = [...this.goldenProfiles];
            this.commonService.savedSourceCrossReferences = [
              ...this.sourceCrossReferences,
            ];
            this.commonService.savedSearchParams = [...this.searchParams];

            this.viewScreen = 'Result';

            this.updateTableData();
            this.cdr.detectChanges();
          }, 0);
        },
        error: (err) => {
          this.commonService.setShowLoader(false);
        },
      });
    }
  }
  updateTableData() {
    this.goldenProfilesMp = new MatTableDataSource<GoldenProfile>(
      this.goldenProfiles,
    );

    this.sourceCrossReferencesMp = new MatTableDataSource<SourceCrossReference>(
      this.sourceCrossReferences,
    );

    if (this.paginator) {
      this.goldenProfilesMp.paginator = this.paginator;
    }

    if (this.rPaginator) {
      this.sourceCrossReferencesMp.paginator = this.rPaginator;
    }

    this.totalItems = this.goldenProfiles.length;
    this.rTotalItems = this.sourceCrossReferences.length;
  }
  patchColumnParams() {
    let gpColumns = localStorage.getItem('golden profile');
    let crColumns = localStorage.getItem('cross reference');
    if (gpColumns != undefined && gpColumns != null && gpColumns != '') {
      gpColumns =
        this.searchProfileService.gpMandatoryCols.join(',') + ',' + gpColumns;
      this.searchForm.patchValue({
        golden_profile: gpColumns,
      });
    } else {
      gpColumns = this.columnModal
        .filter((m) => m.type == 'golden profile')
        .map((m) => m.db_column_name)
        .join(',');
      this.searchForm.patchValue({
        golden_profile: gpColumns,
      });
    }
    if (crColumns != undefined && crColumns != null && crColumns != '') {
      crColumns =
        this.searchProfileService.crMandatoryCols.join(',') + ',' + crColumns;
      this.searchForm.patchValue({
        cross_reference_profile: crColumns,
      });
    } else {
      crColumns = this.columnModal
        .filter((m) => m.type == 'cross reference')
        .map((m) => m.db_column_name)
        .join(',');
      this.searchForm.patchValue({
        cross_reference_profile: crColumns,
      });
    }
  }
  onReset() {
    this.searchForm.reset({
      rcm_id: '',
      ishift: '',
      rmus: '',
      crm: '',
      name: '',
      address: '',
      dea: '',
      hin_no: '',
      id_340b: '',
      npi: '',
      sln: '',
      city: '',
      state: '',
      zip: '',
      type: 'Both',
      source: '',
      source_id: '',
      golden_profile: '',
      cross_reference_profile: '',
      tddd: '',
      hcce: '',
    });
    this.searchForm.updateValueAndValidity();
  }
  checkSearchValid() {
    this.isSearchFormValid = false;
    const objSearch = this.searchForm.value;
    for (const key in objSearch) {
      if (
        key != 'source' &&
        key != 'source_id' &&
        key != 'cross_reference_profile' &&
        key != 'golden_profile' &&
        key != 'type'
      ) {
        if (objSearch[key].trim() != '') {
          this.isSearchFormValid = true;
        }
      }
    }
    if (
      objSearch['source'].trim() != '' &&
      objSearch['source_id'].trim() == ''
    ) {
      this.isSearchFormValid = false;
    }
    if (
      objSearch['source'].trim() != '' &&
      objSearch['source_id'].trim() != ''
    ) {
      this.isSearchFormValid = true;
    }
  }
  setSearchParams() {
    this.searchParams = [];
    for (const key in this.searchForm.value) {
      if (
        this.searchForm.value[key].trim() != '' &&
        key != 'cross_reference_profile' &&
        key != 'golden_profile'
      ) {
        const display = this.commonService.fieldsDisplay.find(
          (fd) => fd.id === key,
        )?.display;
        if (display) {
          const value = this.searchForm.value[key];
          this.searchParams.push({ display: display, value: value });
        }
      }
    }
  }
  // getSources() {
  //   this.commonService.setShowLoader(true);
  //   this.searchProfileService.getSources().subscribe({
  //     next: (res: any) => {
  //       this.commonService.setShowLoader(false);
  //       this.allSources = res.data;
  //       this.initSubscription();
  //     },
  //     error: (error: any) => {
  //       this.commonService.setShowLoader(false);
  //     },
  //   });
  // }
  getSavedPreference() {
    this.commonService.setShowLoader(true);
    this.searchProfileService.getUserColumnPreference().subscribe({
      next: () => {
        this.commonService.setShowLoader(false);
      },
      error: () => {
        this.commonService.setShowLoader(false);
      },
    });
  }
  checkAndShowResult() {
    this.goldenProfiles = JSON.parse(localStorage.getItem('gp')!);
    this.sourceCrossReferences = JSON.parse(localStorage.getItem('cr')!);
    this.onReset();
    this.viewScreen = 'Result';
  }
  // saveToJson(res: SearchColumnResults) {
  //   localStorage.setItem('gp', JSON.stringify(res.golden_profile_results));
  //   localStorage.setItem('cr', JSON.stringify(res.cross_reference_results));
  // }
  goToHistory() {
    this.viewScreen = 'History';
  }
  showBulkSearch() {
    // const modalRef =
    this.modalService.open(BulkSearchModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'md',
      modalDialogClass: 'bu-dialog',
    });
  }
  initSearchSubject() {
    this.spHistorySubscription = this.commonService.spSubject.subscribe(
      (res) => {
        if (!res) return;

        // 🔴 STEP 1: RESET FORM FIRST (IMPORTANT FIX)
        this.searchForm.reset({
          rcm_id: '',
          ishift: '',
          rmus: '',
          crm: '',
          name: '',
          address: '',
          dea: '',
          hin_no: '',
          id_340b: '',
          npi: '',
          sln: '',
          city: '',
          state: '',
          zip: '',
          type: 'Both',
          source: '',
          source_id: '',
          tddd: '',
          hcce: '',
        });
        this.searchForm.patchValue(res);
        this.searchResult();
      },
    );
  }
  onBlur(value: string) {
    const parsedAddress = this.commonService.parseUSAddress(value);
    if (parsedAddress) {
      this.searchForm.get('address')?.setValue(parsedAddress?.street);
      this.searchForm.get('city')?.setValue(parsedAddress?.city);
      this.searchForm.get('state')?.setValue(parsedAddress?.state);
      this.searchForm.get('zip')?.setValue(parsedAddress?.zip);
    }
  }
}
